query JiraGlobalIssueCreateModalLoad(
  $project: JiraProjectInput
  $issueType: JiraIssueTypeInput
  $requestType: JiraRequestTypeInput
  $defaultValues: JiraIssueFieldsInput
) {
  jiraIssueCreateFields(
    fieldsFilter: ADMIN_CONFIGURED_FIELDS
    project: $project
    issueType: $issueType
    requestType: $requestType
    defaultValues: $defaultValues
  ) {
    ... on JiraIssueCreateFields {
      fields {
        nodes {
          ...BaseFieldFragment
          # Type specific fields
          # NOTE: When adding type specific fields, make sure to add them to the list
          # of possible types in packages/atlassian-client/src/delegating-jira-apollo-client.ts
          ...ProjectFieldFragment
          ...IssueTypeFieldFragment
          ...TextFieldFragment
          ...RichTextFieldFragment
          # We need to deprecate EpicLinkFieldFragment in https://product-fabric.atlassian.net/browse/CNS-7997
          ...EpicLinkFieldFragment
          ...ParentLinkFieldFragment
          ...DatePickerFieldFragment
          ...DateTimePickerFieldFragment
          ...ComponentsFieldFragment
          ...SprintFieldFragment
          ...PriorityFieldFragment
          ...LabelFieldFragment
          ...SingleUserPickerFieldFragment
          ...MultiUserPickerFieldFragment
          ...JsmRequestTypeFragment
          ...JiraIssueLinkFieldFragment
          ...JsmOrganizationFieldFragment
          ...JiraSingleSelectFragment
          ...CheckboxesFieldFragment
          ...RadioPickerFieldFragment
          ...MultipleSelectFieldFragment
          ...JiraAtlassianTeamFieldFragment
          __typename
        }
        __typename
      }
      errors {
        identifier
        extensions {
          ... on JiraIssueFieldUnsupportedErrorExtension {
            fieldName
            fieldType
            isRequiredField
            __typename
          }
          ... on JiraIssueFieldValueValidationErrorExtension {
            errorType
            message
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
    ... on QueryError {
      message
      __typename
    }
    __typename
  }
}

fragment BaseFieldFragment on JiraIssueField {
  fieldId
  name
  type
  description
  ... on JiraIssueFieldConfiguration {
    fieldConfig {
      isRequired
      isEditable
      nonEditableReason {
        message
        __typename
      }
      __typename
    }
    __typename
  }
  __typename
}

fragment JiraAtlassianTeamFieldFragment on JiraAtlassianTeamField {
  id
  fieldId
  name
  selectedTeam {
    avatar {
      small
    }
  }
  searchUrl
  __typename
}

fragment ProjectFieldFragment on JiraProjectField {
  project {
    ...Project
    __typename
  }
  searchUrl
  __typename
}

fragment Project on JiraProject {
  id: projectId
  name
  avatar {
    large
    __typename
  }
  projectType
  simplified
  key
  __typename
}

fragment IssueTypeFieldFragment on JiraIssueTypeField {
  issueType {
    ...IssueType
    __typename
  }
  issueTypes {
    nodes {
      ...IssueType
      __typename
    }
    __typename
  }
  __typename
}

fragment IssueType on JiraIssueType {
  id: issueTypeId
  name
  avatar {
    large
    __typename
  }
  __typename
}

fragment TextFieldFragment on JiraSingleLineTextField {
  text
  __typename
}

fragment RichTextFieldFragment on JiraRichTextField {
  richText {
    plainText
    adfValue {
      json
      __typename
    }
    __typename
  }
  renderer
  mediaContext {
    ...JiraMediaContextFragment
    __typename
  }
  __typename
}

fragment JiraMediaContextFragment on JiraMediaContext {
  uploadToken {
    ... on JiraMediaUploadToken {
      endpointUrl
      clientId
      targetCollection
      token
      tokenDurationInMin
      __typename
    }
    ... on QueryError {
      message
      __typename
    }
    __typename
  }
  __typename
}

fragment PriorityFieldFragment on JiraPriorityField {
  priority {
    ...Priority
    __typename
  }
  priorities {
    nodes {
      ...Priority
      __typename
    }
    __typename
  }
  __typename
}

fragment Priority on JiraPriority {
  name
  id: priorityId
  iconUrl
  __typename
}

fragment SprintFieldFragment on JiraSprintField {
  selectedSprints {
    ...Sprint
    __typename
  }
  searchUrl
  __typename
}

fragment Sprint on JiraSprint {
  sprintId
  name
  state
  __typename
}

fragment LabelFieldFragment on JiraLabelsField {
  selectedLabels {
    ...Label
    __typename
  }
  searchUrl
  __typename
}

fragment Label on JiraLabel {
  id: labelId
  name
  __typename
}

fragment SingleUserPickerFieldFragment on JiraSingleSelectUserPickerField {
  user {
    ...UserFragment
    __typename
  }
  searchUrl
  __typename
}

fragment UserFragment on User {
  accountId
  name
  picture
  __typename
}

fragment MultiUserPickerFieldFragment on JiraMultipleSelectUserPickerField {
  selectedUsers {
    ...UserFragment
    __typename
  }
  searchUrl
  __typename
}

fragment EpicLinkFieldFragment on JiraEpicLinkField {
  epic {
    ...Epic
    __typename
  }
  searchUrl
  __typename
}

fragment Epic on JiraEpic {
  name
  key
  __typename
}

fragment ParentLinkFieldFragment on JiraParentIssueField {
  name
  __typename
}

fragment DatePickerFieldFragment on JiraDatePickerField {
  date
  __typename
}

fragment DateTimePickerFieldFragment on JiraDateTimePickerField {
  dateTime
  __typename
}

fragment ComponentsFieldFragment on JiraComponentsField {
  selectedComponents {
    ...Component
    __typename
  }
  components {
    nodes {
      ...Component
      __typename
    }
    __typename
  }
  __typename
}

fragment Component on JiraComponent {
  id: componentId
  name
  __typename
}

fragment JsmRequestTypeFragment on JsmRequestTypeField {
  requestType {
    ...RequestType
    __typename
  }
  requestTypes {
    nodes {
      ...RequestType
      __typename
    }
    __typename
  }
  __typename
}

fragment RequestType on JsmRequestType {
  avatar {
    large
    __typename
  }
  issueType {
    issueTypeId
    __typename
  }
  key
  name
  description
  requestTypeId
}

fragment JiraIssueLinkFieldFragment on JiraIssueLinkField {
  issueLinks {
    relatedBy {
      ...JiraIssueLinkType
      __typename
    }
  }
  issueLinkTypeRelations {
    nodes {
      ...JiraIssueLinkType
      __typename
    }
    __typename
  }
  name
  searchUrl
  __typename
}

fragment JiraIssueLinkType on JiraIssueLinkTypeRelation {
  direction
  linkTypeId
  linkTypeName
  relationName
}

fragment JsmOrganizationFieldFragment on JsmOrganizationField {
  searchUrl
  __typename
}

fragment JiraSingleSelectFragment on JiraSingleSelectField {
  fieldOption {
    ...JiraOptionFragment
    __typename
  }
  fieldOptions {
    ...JiraOptionConnectionFragment
    __typename
  }
}

fragment JiraOptionConnectionFragment on JiraOptionConnection {
  edges {
    node {
      ...JiraOptionFragment
      __typename
    }
  }
}

fragment JiraOptionFragment on JiraOption {
  id
  value
  optionId
  isDisabled
}

fragment CheckboxesFieldFragment on JiraCheckboxesField {
  # Ideally we would limit the results using the searchBy argument, however,
  # this argument is currently feature flagged as the rollout is not complete.
  # We will need to make changes once the rollout is finished.
  # Rollout epic: https://jplat.jira.atlassian.cloud/browse/JIV-13958
  fieldOptions {
    ...JiraOptionConnectionFragment
    __typename
  }
  __typename
}

fragment MultipleSelectFieldFragment on JiraMultipleSelectField {
  # Ideally we would limit the results using the searchBy argument, however,
  # this argument is currently feature flagged as the rollout is not complete.
  # We will need to make changes once the rollout is finished.
  # Rollout epic: https://jplat.jira.atlassian.cloud/browse/JIV-13958
  fieldOptions {
    ...JiraOptionConnectionFragment
    __typename
  }
  __typename
}

fragment RadioPickerFieldFragment on JiraRadioSelectField {
  selectedOption {
    ...JiraOptionFragment
    __typename
  }
  fieldOptions {
    edges {
      node {
        ...JiraOptionFragment
        __typename
      }
    }
    __typename
  }
}
